'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/states';
import { elowenClient } from '../../lib/elowenClient';
import { interpolate, useTranslation } from '../../lib/i18n';
import { authGeneration } from '../../lib/token';

function highlighted(text: string, query: string): ReactNode[] {
  const result: ReactNode[] = [];
  const lower = text.toLocaleLowerCase();
  const needle = query.toLocaleLowerCase();
  let start = 0;
  let match = lower.indexOf(needle);
  while (match !== -1) {
    result.push(text.slice(start, match), <mark key={match}>{text.slice(match, match + needle.length)}</mark>);
    start = match + needle.length;
    match = lower.indexOf(needle, start);
  }
  result.push(text.slice(start));
  return result;
}

/** Search remains session-scoped in the host query cache; cursors are issued by the server. */
export function ConversationSearch({ session, onJump, onClose }: {
  session: string;
  onJump: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [selected, setSelected] = useState(-1);
  const query = input.trim();
  const results = useInfiniteQuery({
    queryKey: ['conversation-message-search', authGeneration(), session, query],
    queryFn: ({ pageParam, signal }) => elowenClient.brainMessagesSearch(
      session, query, pageParam === undefined ? { limit: 50 } : { limit: 50, before: pageParam }, signal,
    ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextBefore ?? undefined,
    enabled: query.length >= 2 && query.length <= 500,
    retry: false,
  });
  const hits = results.data?.pages.flatMap((page) => page.items) ?? [];
  const count = interpolate(t.brainChat.ux.searchCount, { count: hits.length });
  const step = (direction: number) => {
    if (!hits.length) return;
    const index = selected < 0 ? (direction > 0 ? 0 : hits.length - 1) : (selected + direction + hits.length) % hits.length;
    setSelected(index);
    onJump(hits[index]!.messageId);
  };
  return (
    <section aria-label={t.brainChat.ux.searchTitle} className="flex min-w-0 flex-col gap-2 border-b border-border p-2">
      <div className="flex items-center gap-2">
        <Input autoFocus data-keyboard-claimed aria-label={t.brainChat.ux.searchTitle} placeholder={t.brainChat.ux.searchPlaceholder}
          value={input} maxLength={500} onChange={(event) => { setInput(event.target.value); setSelected(-1); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); }
            else if (event.key === 'Enter' && !event.nativeEvent.isComposing && !event.ctrlKey && !event.altKey && !event.metaKey) { event.preventDefault(); event.stopPropagation(); step(event.shiftKey ? -1 : 1); }
          }} />
        {hits.length > 0 ? <span className="shrink-0 tabular-nums text-meta text-muted-foreground" title={count}>
          <span aria-hidden>{hits.length}{results.hasNextPage ? '+' : ''}</span>
          <span className="sr-only">{count}</span>
        </span> : null}
        <Button type="button" variant="ghost" size="icon" onClick={() => step(-1)} disabled={!hits.length} aria-label={t.brainChat.ux.searchPrevious}><ArrowUp size={16} aria-hidden /></Button>
        <Button type="button" variant="ghost" size="icon" onClick={() => step(1)} disabled={!hits.length} aria-label={t.brainChat.ux.searchNext}><ArrowDown size={16} aria-hidden /></Button>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label={t.common.close}><X size={16} aria-hidden /></Button>
      </div>
      {query.length >= 2 && results.isPending ? <Spinner label={t.common.loading} /> : null}
      {results.isError ? <div role="alert" className="flex items-center gap-2 text-meta">
        <span>{t.brainChat.ux.searchFailed}</span>
        <Button type="button" variant="ghost" onClick={() => { void (results.isFetchNextPageError ? results.fetchNextPage() : results.refetch()); }}>{t.common.retry}</Button>
      </div> : null}
      {results.isSuccess && hits.length === 0 ? <p className="text-meta">{t.brainChat.ux.searchEmpty}</p> : null}
      {hits.length > 0 ? <div className="max-h-48 overflow-y-auto">
        {hits.map((hit, index) => <Button type="button" variant="ghost" key={hit.messageId}
          className="block h-auto w-full whitespace-normal rounded-md p-2 text-left text-foreground"
          aria-current={selected === index ? 'true' : undefined}
          onClick={() => { setSelected(index); onJump(hit.messageId); }}>{highlighted(hit.snippet, query)}</Button>)}
      </div> : null}
      {results.hasNextPage ? <Button type="button" variant="ghost" disabled={results.isFetchingNextPage}
        onClick={() => { void results.fetchNextPage(); }}>{t.brainChat.ux.searchMore}</Button> : null}
    </section>
  );
}
